import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import { selectionSet, type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { context } from '@adornis/baseql/server/context.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { mailer } from '@adornis/buchungsheld-db/mails/communication.js';
import { mongo } from '@adornis/buchungsheld-db/server/mongo.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { prepareMessage } from '@adornis/mails/server/communication.js';
import sanitizeHtml from 'sanitize-html';
import * as m from '../../localization/out/messages.js';
import { composeFreeFormMail, loadFreeFormMailLogoConfig, toMailSafeHtml } from '../mails/free-form-mail-html.js';
import { FREE_FORM_MAIL_MAX_RECIPIENTS } from '../shared/free-form-mail.js';
import { assertCallerIsAdmin } from './admin-guard.js';
import { buildUserListStages } from './admin-users.js';

@Entity()
export class FreeFormMailRecipient extends AdornisEntity {
  static override _class = 'FreeFormMailRecipient';

  @Field(() => ID)
  _id!: string;

  @Field(() => String)
  email!: string;

  @Field(() => String)
  firstName?: Maybe<string>;

  @Field(() => String)
  lastName?: Maybe<string>;
}

@Entity()
export class FreeFormMailSendResult extends AdornisEntity {
  static override _class = 'FreeFormMailSendResult';

  @Field(() => Int)
  sent!: number;

  @Field(() => Int)
  failed!: number;
}

export const resolveFreeFormMailRecipients = registerQuery({
  operationName: 'resolveFreeFormMailRecipients',
  type: () => [FreeFormMailRecipient],
  params: [
    { name: 'userIDs', type: () => [ID] },
    { name: 'filter', type: () => AdornisFilter },
    { name: 'applicationStatus', type: () => String },
    { name: 'interviewOutcome', type: () => String },
    { name: 'createdYear', type: () => Int },
    { name: 'activeOnly', type: () => Boolean },
  ],
  resolve:
    (
      userIDs: Maybe<string[]>,
      filter: Maybe<AdornisFilter>,
      applicationStatus: Maybe<string>,
      interviewOutcome: Maybe<string>,
      createdYear: Maybe<number>,
      activeOnly: Maybe<boolean>,
    ) =>
    async (_gqlFields: BaseQLSelectionSet<FreeFormMailRecipient>) => {
      await assertCallerIsAdmin();
      const locale = 'de' as const;
      if (!!userIDs?.length === !!filter) {
        throw new Error(m.mail_admin_recipient_source({}, { locale }));
      }
      const stages = filter
        ? [
            { $match: filter.getMongoFilter() },
            ...buildUserListStages({ applicationStatus, interviewOutcome, createdYear, activeOnly }),
          ]
        : [{ $match: { _id: { $in: [...new Set(userIDs)] } } }, ...buildUserListStages({})];
      const users = await mongo()
        .collection(User._collectionName)
        .aggregate([
          ...stages,
          { $sort: { email: 1, _id: 1 } },
          { $limit: FREE_FORM_MAIL_MAX_RECIPIENTS + 1 },
          { $project: { _id: 1, email: 1, firstName: 1, lastName: 1 } },
        ])
        .toArray();
      return users
        .filter(user => typeof user.email === 'string' && user.email.trim())
        .map(user => new FreeFormMailRecipient(user));
    },
});

export const previewFreeFormMail = registerQuery({
  operationName: 'previewFreeFormMail',
  type: () => String,
  params: [
    { name: 'subject', type: () => String },
    { name: 'bodyHtml', type: () => String },
    { name: 'userID', type: () => ID },
  ],
  resolve: (subject: Maybe<string>, bodyHtml: Maybe<string>, userID: Maybe<string>) => async () => {
    await assertCallerIsAdmin();
    const locale = 'de' as const;
    if (!userID) throw new Error(m.mail_admin_recipient_required({}, { locale }));
    const [recipient] = await resolveFreeFormMailRecipients(
      [userID],
      null,
      null,
      null,
      null,
      null,
    )(selectionSet(() => FreeFormMailRecipient, 1));
    if (!recipient) throw new Error(m.mail_admin_recipient_not_found({}, { locale }));
    const mail = composeFreeFormMail({
      subject: subject ?? '',
      bodyHtml: bodyHtml ?? '',
      recipient,
      logoConfig: await loadFreeFormMailLogoConfig(),
    });
    return (await prepareMessage(mail)).html;
  },
});

type FreeFormMailClaim = {
  _id: string;
  adminUserID: string | undefined;
  requestID: string;
  status: 'pending' | 'done';
  result?: { sent: number; failed: number };
  createdAt: Date;
};

export const sendFreeFormMailToUsers = registerMutation({
  operationName: 'sendFreeFormMailToUsers',
  type: () => FreeFormMailSendResult,
  params: [
    { name: 'userIDs', type: () => [ID] },
    { name: 'subject', type: () => String },
    { name: 'bodyHtml', type: () => String },
    { name: 'requestID', type: () => String },
  ],
  resolve:
    (userIDs: Maybe<string[]>, subject: Maybe<string>, bodyHtml: Maybe<string>, requestID: Maybe<string>) =>
    async (_gqlFields: BaseQLSelectionSet<FreeFormMailSendResult>) => {
      await assertCallerIsAdmin();
      const locale = 'de' as const;
      if (typeof requestID !== 'string' || !requestID.trim() || requestID.length > 100)
        throw new Error(m.mail_admin_request_id_invalid({}, { locale }));
      if (!subject?.trim()) throw new Error(m.mail_admin_subject_required({}, { locale }));
      if (
        !bodyHtml ||
        !sanitizeHtml(toMailSafeHtml(bodyHtml), { allowedTags: [], allowedAttributes: {} })
          .replace(/&nbsp;|&#160;|&#x0*a0;/gi, '')
          .trim()
      ) {
        throw new Error(m.mail_admin_body_required({}, { locale }));
      }
      const ids = [...new Set(userIDs ?? [])].sort();
      if (!ids.length) throw new Error(m.mail_admin_recipients_required({}, { locale }));
      if (ids.length > FREE_FORM_MAIL_MAX_RECIPIENTS) throw new Error(m.mail_admin_recipients_limit({}, { locale }));
      const adminUserID: string | undefined = context.serverContext ? undefined : context.userID;
      const key = `${adminUserID}:${requestID}`;
      const claims = mongo().collection<FreeFormMailClaim>('mw-free-form-mail-sends');
      try {
        await claims.insertOne({ _id: key, adminUserID, requestID, status: 'pending', createdAt: new Date() });
      } catch (error) {
        if ((error as { code?: number }).code !== 11000) throw error;
        const existing = await claims.findOne({ _id: key });
        if (existing?.status === 'done' && existing.result) return new FreeFormMailSendResult(existing.result);
        throw new Error(m.mail_admin_send_pending({}, { locale }));
      }

      let sent = 0;
      let failed = 0;
      try {
        const recipients = await resolveFreeFormMailRecipients(
          ids,
          null,
          null,
          null,
          null,
          null,
        )(selectionSet(() => FreeFormMailRecipient, 1));
        if (!recipients.length) throw new Error(m.mail_admin_recipients_not_found({}, { locale }));
        const logoConfig = await loadFreeFormMailLogoConfig();
        const configuredMailer = await mailer;
        if (!configuredMailer) throw new Error(m.mail_admin_mailer_missing({}, { locale }));
        for (const [index, recipient] of recipients.entries()) {
          try {
            const mail = composeFreeFormMail({ subject, bodyHtml, recipient, logoConfig });
            const message = await prepareMessage(mail);
            await configuredMailer.sendMail(message, new Recipient(recipient.email));
            sent++;
          } catch (err) {
            logger.warn({ err, userID: recipient._id }, 'Free-form mail recipient failed');
            failed++;
          }
          if (index < recipients.length - 1) await new Promise<void>(resolve => setTimeout(resolve, 50));
        }
        logger.info({ sent, failed, adminUserID }, 'Free-form mail send completed');
        await claims.updateOne({ _id: key }, { $set: { status: 'done', result: { sent, failed } } });
        return new FreeFormMailSendResult({ sent, failed });
      } catch (error) {
        // A failed setup may be retried; never resend after a partial delivery.
        if (sent === 0) await claims.deleteOne({ _id: key });
        throw error;
      }
    },
});
